/**
 * FoundryPage — /foundry spoke route.
 * Full-depth narrative for The Foundry flagship offer.
 */
import { Helmet } from 'react-helmet-async';
import { Link } from '@tanstack/react-router';
import SchemaJsonLd from '../components/primitives/SchemaJsonLd.jsx';
import Eyebrow from '../components/ui/Eyebrow.jsx';
import Button from '../components/ui/Button.jsx';
import Reveal from '../components/ui/Reveal.jsx';
import {
  foundryHead,
  foundryIntro,
  foundryProblem,
  foundryHow,
  foundryWho,
  foundryOutcomes,
  foundryNot,
  foundryCta,
  foundryLinks,
} from '../content/foundry.js';

const foundryServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'The Foundry',
  description:
    'A six-week founder cohort. Five founders per run. Build production-grade AI systems into your business alongside four other founders and walk out with the capability to keep building as the AI landscape evolves.',
  provider: { '@type': 'Organization', name: "The Artificer's AI" },
  areaServed: [
    { '@type': 'Country', name: 'Australia' },
    { '@type': 'AdministrativeArea', name: 'Global' },
  ],
  serviceType: 'AI founder cohort',
};

const foundryBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://artificersai.com/' },
    { '@type': 'ListItem', position: 2, name: 'The Foundry', item: 'https://artificersai.com/foundry' },
  ],
};

export default function FoundryPage() {
  return (
    <>
      <Helmet>
        <title>The Foundry — Six-Week AI Cohort for Founders | The Artificer's AI</title>
        <meta name="description" content="A six-week founder cohort. Build production-grade AI systems into your business alongside four other founders." />
        <link rel="canonical" href="https://artificersai.com/foundry" />
        <meta property="og:title" content="The Foundry — Six-Week AI Cohort for Founders" />
        <meta property="og:description" content="A six-week founder cohort. Build production-grade AI systems into your business alongside four other founders." />
        <meta property="og:url" content="https://artificersai.com/foundry" />
      </Helmet>

      {/* Hero */}
      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <Reveal as="div" delay={0} className="mb-4"><Eyebrow>{foundryHead.eyebrow}</Eyebrow></Reveal>
          <Reveal as="h1" delay={80} className="text-display-lg font-display font-semibold mb-4">{foundryHead.heading}</Reveal>
          <Reveal as="p" delay={140} className="text-body-lg text-ink-200 mb-8">{foundryHead.subheading}</Reveal>
          <Reveal as="p" delay={200} className="text-body text-ink-200 mb-8">{foundryIntro}</Reveal>
          <Reveal delay={260}><Button as={Link} to="/intake">{foundryCta.label}</Button></Reveal>
        </div>
      </section>

      {/* What the Foundry solves */}
      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{foundryProblem.heading}</Reveal>
          <Reveal>
            {foundryProblem.body.split('\n\n').map((p, i) => (
              <p key={i} className="text-body text-ink-200 mb-4">{p}</p>
            ))}
          </Reveal>
          <Reveal as="p" delay={80} className="text-body-lg font-display font-semibold text-ink-50 mt-8">{foundryProblem.hook}</Reveal>
        </div>
      </section>

      {/* How it runs */}
      <section className="section-pad bg-surface text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{foundryHow.heading}</Reveal>
          <Reveal as="p" delay={60} className="text-body text-ink-200 mb-8">{foundryHow.intro}</Reveal>
          <div className="flex flex-col gap-6">
            {foundryHow.weeks.map((week, i) => (
              <Reveal key={week.label} delay={Math.min(i * 80, 300)}>
                <div className="border-l-2 border-brand-blue-bright/30 pl-6">
                  <span className="font-mono text-mono-xs text-brand-blue-bright uppercase tracking-wider">{week.label}</span>
                  <h3 className="text-h3 font-display font-semibold text-ink-50 mt-1 mb-2">{week.title}</h3>
                  <p className="text-body-sm text-ink-200">{week.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who goes through it */}
      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{foundryWho.heading}</Reveal>
          <Reveal>
            {foundryWho.paragraphs.map((p, i) => (
              <p key={i} className="text-body text-ink-200 mb-4">{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* What you walk out with */}
      <section className="section-pad bg-surface text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{foundryOutcomes.heading}</Reveal>
          <ul className="flex flex-col gap-4">
            {foundryOutcomes.items.map((item, i) => (
              <Reveal as="li" key={i} delay={i * 80} className="text-body text-ink-200 pl-4 border-l border-white/[.08]">{item}</Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* What the Foundry is not */}
      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{foundryNot.heading}</Reveal>
          <ul className="flex flex-col gap-3">
            {foundryNot.items.map((item, i) => (
              <Reveal as="li" key={i} delay={i * 80} className="text-body text-ink-200">{item}</Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Cross-links + CTA */}
      <section className="section-pad bg-surface text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <div className="flex flex-col gap-4 mb-10">
            <Reveal delay={0}><Link to={foundryLinks.teamTraining.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
              {foundryLinks.teamTraining.label}
            </Link></Reveal>
            <Reveal delay={80}><Link to={foundryLinks.automatedSystems.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
              {foundryLinks.automatedSystems.label}
            </Link></Reveal>
            <Reveal delay={160}><Link to={foundryLinks.systems.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
              {foundryLinks.systems.label}
            </Link></Reveal>
          </div>
          <Reveal delay={240}><Button as={Link} to="/intake">{foundryCta.label}</Button></Reveal>
        </div>
      </section>

      <SchemaJsonLd data={foundryServiceSchema} />
      <SchemaJsonLd data={foundryBreadcrumb} />
    </>
  );
}
